/**
 * Local Storage 工具函数
 * 提供类型安全的读写方法和配额超限处理
 */

// 存储配额警告阈值（5MB）
const QUOTA_WARNING_THRESHOLD = 5 * 1024 * 1024

/**
 * 检查 localStorage 是否可用
 */
export function isStorageAvailable(): boolean {
  try {
    const test = '__storage_test__'
    localStorage.setItem(test, test)
    localStorage.removeItem(test)
    return true
  } catch {
    return false
  }
}

/**
 * 获取当前 localStorage 使用量（字节）
 */
export function getStorageUsage(): number {
  let total = 0
  for (const key in localStorage) {
    if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
      total += localStorage.getItem(key)?.length ?? 0
    }
  }
  return total * 2 // UTF-16 编码，每个字符 2 字节
}

/**
 * 检查是否接近配额限制
 */
export function isNearQuotaLimit(): boolean {
  return getStorageUsage() > QUOTA_WARNING_THRESHOLD
}

/**
 * 类型安全的读取方法
 */
export function getItem<T>(key: string, defaultValue: T): T {
  if (!isStorageAvailable()) {return defaultValue}

  try {
    const item = localStorage.getItem(key)
    if (item === null) {return defaultValue}
    return JSON.parse(item) as T
  } catch {
    return defaultValue
  }
}

/**
 * 类型安全的写入方法
 * 返回是否成功写入
 */
export function setItem<T>(key: string, value: T): boolean {
  if (!isStorageAvailable()) {return false}

  try {
    localStorage.setItem(key, JSON.stringify(value))
    return true
  } catch (error) {
    // 配额超限处理
    if (isQuotaExceededError(error)) {
      console.warn(`Storage quota exceeded when setting key: ${key}`)
      return false
    }
    throw error
  }
}

/**
 * 带配额超限自动清理的写入方法
 * 如果配额超限，会尝试清理过期数据后重试
 */
export function setItemWithCleanup<T>(
  key: string,
  value: T,
  cleanupFn?: () => void
): boolean {
  if (!isStorageAvailable()) {return false}

  try {
    localStorage.setItem(key, JSON.stringify(value))
    return true
  } catch (error) {
    if (isQuotaExceededError(error)) {
      // 尝试清理
      if (cleanupFn) {
        cleanupFn()
      } else {
        // 默认清理策略：移除最旧的项目
        clearOldestItems(1)
      }

      // 重试
      try {
        localStorage.setItem(key, JSON.stringify(value))
        return true
      } catch {
        console.error(`Failed to set item after cleanup: ${key}`)
        return false
      }
    }
    throw error
  }
}

/**
 * 移除项目
 */
export function removeItem(key: string): void {
  if (!isStorageAvailable()) {return}
  localStorage.removeItem(key)
}

/**
 * 清除所有项目
 */
export function clear(): void {
  if (!isStorageAvailable()) {return}
  localStorage.clear()
}

/**
 * 获取所有键
 */
export function getAllKeys(): string[] {
  if (!isStorageAvailable()) {return []}
  return Object.keys(localStorage)
}

/**
 * 获取带前缀的所有键
 */
export function getKeysWithPrefix(prefix: string): string[] {
  return getAllKeys().filter((key) => key.startsWith(prefix))
}

/**
 * 清除带前缀的所有项目
 */
export function clearWithPrefix(prefix: string): void {
  const keys = getKeysWithPrefix(prefix)
  for (const key of keys) {
    removeItem(key)
  }
}

/**
 * 检查是否为配额超限错误
 */
function isQuotaExceededError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    (error.code === 22 || // 旧版浏览器
      error.code === 1014 || // Firefox
      error.name === 'QuotaExceededError' ||
      error.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  )
}

/**
 * 清除最旧的项目
 */
function clearOldestItems(count: number): void {
  const keys = getAllKeys()
  const items: { key: string; size: number }[] = []

  for (const key of keys) {
    const value = localStorage.getItem(key)
    if (value) {
      items.push({ key, size: value.length })
    }
  }

  // 按大小排序，优先删除大的项目
  items.sort((a, b) => b.size - a.size)

  for (let i = 0; i < Math.min(count, items.length); i++) {
    localStorage.removeItem(items[i].key)
  }
}

/**
 * 带过期时间的存储项
 */
interface StorageItemWithExpiry<T> {
  value: T
  expiresAt: number
}

/**
 * 设置带过期时间的项目
 */
export function setItemWithExpiry<T>(
  key: string,
  value: T,
  ttlMs: number
): boolean {
  const item: StorageItemWithExpiry<T> = {
    value,
    expiresAt: Date.now() + ttlMs,
  }
  return setItem(key, item)
}

/**
 * 获取带过期时间的项目
 * 如果已过期，返回 null 并清除
 */
export function getItemWithExpiry<T>(key: string): T | null {
  const item = getItem<StorageItemWithExpiry<T> | null>(key, null)
  if (!item) {return null}

  if (Date.now() > item.expiresAt) {
    removeItem(key)
    return null
  }

  return item.value
}

/**
 * 清理所有过期项目
 */
export function cleanupExpiredItems(prefix?: string): number {
  const keys = prefix ? getKeysWithPrefix(prefix) : getAllKeys()
  let cleaned = 0

  for (const key of keys) {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) {continue}

      const item = JSON.parse(raw) as { expiresAt?: number }
      if (item.expiresAt && Date.now() > item.expiresAt) {
        removeItem(key)
        cleaned++
      }
    } catch {
      // 忽略解析错误
    }
  }

  return cleaned
}
